import React from "react";
import { Card, Tag, Typography, Row, Col, Space } from "antd";
import {
  EnvironmentOutlined,
  PhoneOutlined,
  WhatsAppOutlined,
  ClockCircleOutlined,
  ShoppingOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import "./PharmacyCard.css";

const { Title, Text, Link } = Typography;

const PharmacyCard = ({ pharmacy }) => {
  const {
    name,
    address_line1,
    locality,
    city_town,
    district,
    state,
    pincode,
    phone_country_code,
    phone_number,
    whatsapp_country_code,
    whatsapp_number,
    google_maps_link,
    services,
    order_methods,
    open_from,
    open_to,
    is_open_24_hours, // Destructure the new prop
  } = pharmacy;

  const fullAddress = `${address_line1}${locality ? `, ${locality}` : ""}${
    city_town ? `, ${city_town}` : ""
  }${district ? `, ${district}` : ""}, ${state} - ${pincode}`;

  const formatPhoneNumber = (code, number) => {
    if (!number) return "N/A";
    const countryCode = code || "+91";
    return `${countryCode} ${number}`;
  };

  const getWhatsAppLink = (code, number) => {
    if (!number) return null;
    const countryCode = code || "+91";
    const fullNumber = `${countryCode}${number}`;
    return `https://wa.me/${fullNumber.replace(/\D/g, "")}`;
  };

  const formatLabel = (str) =>
    str
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");

  const renderTags = (items, color) => {
    if (!items) return <Text type="secondary">N/A</Text>;
    return items.split(",").map((item) => (
      <Tag color={color} key={item.trim()} style={{ margin: "2px" }}>
        {formatLabel(item.trim())}
      </Tag>
    ));
  };

  const formatTo12Hour = (timeStr) => {
    if (!timeStr) return "N/A";
    const [hour, minute] = timeStr.split(":");
    const date = new Date();
    date.setHours(+hour);
    date.setMinutes(+minute);
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  // Determine timings display based on is_open_24_hours
  const timings = is_open_24_hours
    ? "Open 24 hours"
    : open_from && open_to
    ? `${formatTo12Hour(open_from)} - ${formatTo12Hour(open_to)}`
    : "Timings not available";

  // Tag color for 24/7 status
  const timingTagColor = is_open_24_hours ? "green" : "default";

  return (
    <Card hoverable className="pharmacy-details-card compact">
      <div className="card-header">
        <Link
          href={
            google_maps_link ||
            `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
              name + " " + fullAddress
            )}`
          }
          target="_blank"
          rel="noopener noreferrer"
          className="pharmacy-name-link"
        >
          {name} <EnvironmentOutlined />
        </Link>
        <Tag color={timingTagColor} className="pharmacy-timings-tag">
          <ClockCircleOutlined style={{ marginRight: 5 }} />
          {timings}
        </Tag>
      </div>

      <div className="card-section address-section">
        <Text>{fullAddress}</Text>
      </div>

      <Row gutter={[16, 8]} className="card-section contact-section">
        <Col xs={24} sm={whatsapp_number ? 12 : 24}>
          <Space direction="vertical" size="small">
            <Text strong>
              <PhoneOutlined /> Phone:
            </Text>
            <Link
              href={`tel:${(phone_country_code || "+91")}${phone_number}`}
            >
              {formatPhoneNumber(phone_country_code, phone_number)}
            </Link>
          </Space>
        </Col>
        {whatsapp_number && (
          <Col xs={24} sm={12}>
            <Space direction="vertical" size="small">
              <Text strong>
                <WhatsAppOutlined /> WhatsApp:
              </Text>
              <Link
                href={getWhatsAppLink(whatsapp_country_code, whatsapp_number)}
                target="_blank"
                rel="noopener noreferrer"
              >
                Chat on WhatsApp
              </Link>
            </Space>
          </Col>
        )}
      </Row>

      <Row gutter={[16, 8]} className="card-section features-section">
        <Col xs={24} sm={12}>
          <Text strong>
            <ShoppingOutlined /> Order Methods:
          </Text>
          <div>{renderTags(order_methods, "blue")}</div>
        </Col>
        <Col xs={24} sm={12}>
          <Text strong>
            <CheckCircleOutlined /> Services Offered:
          </Text>
          <div>{renderTags(services, "green")}</div>
        </Col>
      </Row>
    </Card>
  );
};

export default PharmacyCard;